/* eslint-disable @typescript-eslint/no-explicit-any */
// Account - Favourites Page -  Auth Page

import React from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { ApolloProvider } from '@apollo/client';
import { inspect } from 'util';

import { ResponsiveGrid } from '@adobe/aem-react-editable-components';

import { respGridUtil, getProcessEnvs, prePageCall, Layout, logger } from '@marriott/mi-headless-utils';

import { getDeployedEnvType, useMiApolloClient } from '@marriott/mi-apollo-client-utils';

import { constants } from '@marriott/mi-account-components/constants';

import { addSubDirectoryPrefix, preProcessorCall, setSubDirectoryPrefix } from '@marriott/mi-account-components/utils';

import { signInModel } from '../../_localDevMocks';
import operationSignatures from '../../_constants/operationSignatures.json';

const { NEXT_PUBLIC_AEM_PATH, SKIP_PREPROCESSOR_CALL, IS_LOCAL_DEV } = getProcessEnvs();
const { ITEM_PATH_ROOT, UNAUTH_URL, NEXT_PUBLIC_ENV_KEYS } = constants;

global.loggerInstance = logger({}) as CallableFunction;
const { log } = global.loggerInstance('SignInErrorPage');

export default function SignInOverlay({ model, deployedEnvType }) {
  const MiApolloClient = useMiApolloClient(operationSignatures, deployedEnvType);

  const {
    metaNames,
    metaProperties,
    title,
    staticComponentClientLibsCSS,
    staticComponentClientLibsJS,
    gridCss,
    authorMode,
  } = model;
  const responsiveGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const router = useRouter();
  const overlay = router && router.query['overlay'];

  if (model) {
    const subDirectoryPrefix = model.subDirectoryPrefix ? model.subDirectoryPrefix : '';
    setSubDirectoryPrefix(subDirectoryPrefix);
  }

  return (
    <>
      {!overlay ? (
        <iframe
          src={addSubDirectoryPrefix('/signInOverlay.mi?overlay=true')}
          title="signin"
          frameBorder="0"
          allowFullScreen
          id="signInOverlayIframe"
          width={'100%'}
        ></iframe>
      ) : (
        <ApolloProvider client={MiApolloClient}>
          <Layout
            metaNames={metaNames}
            metaProps={metaProperties}
            title={title}
            staticComponentClientLibsCSS={staticComponentClientLibsCSS}
            staticComponentClientLibsJS={staticComponentClientLibsJS}
            gridCss={gridCss}
            isAuthorMode={authorMode}
          >
            <ResponsiveGrid
              {...responsiveGridModel}
              model={responsiveGridModel}
              pagePath={NEXT_PUBLIC_AEM_PATH}
              itemPath={ITEM_PATH_ROOT}
              customClassName="account-page-wrapper"
            />
          </Layout>
        </ApolloProvider>
      )}
    </>
  );
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
const SignInOverlaySpecificData = async request => {
  const deployedEnvType = getDeployedEnvType();
  let response: any = { operationSignatures: [], deployedEnvType };
  const resolvedUrlString: string = request?.req?.url;
  const isAuthorMode = resolvedUrlString?.includes('isAuthorMode');
  const urlQueryParameters = new URLSearchParams(request?.query)?.toString();
  const preprocessorPath = urlQueryParameters ? `${UNAUTH_URL}?${urlQueryParameters}` : UNAUTH_URL;

  /**
   * Preprocessor Call Start
   */

  if (SKIP_PREPROCESSOR_CALL !== 'true' && IS_LOCAL_DEV !== 'true' && !isAuthorMode) {
    // Making page specific preprocessor call
    log.debug(`[SignInOverlaySpecificData] session Id: ${request?.req?.cookies?.sessionID}`);
    const preprocessorResponse = await preProcessorCall(
      request?.req?.cookies?.sessionID,
      request?.req,
      preprocessorPath,
      {}
    );
    log.debug(`[SignInOverlaySpecificData] PreprocessorResponse: ${inspect(preprocessorResponse)}`);
    if (preprocessorResponse?.data?.error) {
      const error = {
        redirect: { redirect: preprocessorResponse?.data?.redirect },
        value: true,
      };
      response = { operationSignatures: [], deployedEnvType, error };
    } else if (preprocessorResponse?.data) {
      response = {
        operationSignatures: [],
        deployedEnvType,
        isEAA: preprocessorResponse?.data?.isEAA,
        isMobileAuthFeatureEnabled: preprocessorResponse?.data?.isMobileAuthFeatureEnabled,
        nextPublicEnvKeys: NEXT_PUBLIC_ENV_KEYS,
      };
    }
  }
  /**
   * Preprocessor Call End
   */
  return response;
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
export const getServerSideProps: GetServerSideProps<any> = prePageCall(SignInOverlaySpecificData, signInModel);
